import useDiscoveryFetch from './useDiscoveryFetch';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Form, Row, Col, Card, Modal, Button } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import Pagination from 'react-bootstrap/Pagination';
import { useMediaQuery } from 'react-responsive';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import api from '../../api/axiosInstance';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import DiscoveryFilterBar from './DiscoveryFilterBar';
import DiscoveryMap from './DiscoveryMap';
import MobileDiscoveryHeading from './MobileDiscoveryHeading';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileDiscoveryCityCard from './MobileDiscoveryCityCard';
import useMobileDiscoveryNavDocking from './useMobileDiscoveryNavDocking';
import useMobileLoadMore from './useMobileLoadMore';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import MobileAppliedChips from './MobileAppliedChips';
import DiscoveryDesktopAiSlot from './DiscoveryDesktopAiSlot';
import NavTabs from '../../NavTabs';
import icFilter from '../../assets/img/ic-filter.svg';
import icSort from '../../assets/img/ic-sort.svg';
import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import { MAPBOX_TOKEN } from '../../config/mapbox';
import countriesData from '../../data/countries';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import { mobileCitySortDefinitions, getMobileSortedCities } from './mobileCitySort';
import { getCityCoords } from './cityMapCoords';

const PAGE_DISCOVERY_TYPE = 'cities';

const DiscoverCityList = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [countryFilter] = useState(searchParams.get('country') || '');
    // Keep the last explicitly applied comparator when fresh API data arrives.
    const refreshSortRef = useRef(null);
    const [citiesList, setCitiesList] = useState([]);
    const [sortOrderCity, setSortOrderCity] = useState('asc');
    const [sortOrderCountry, setSortOrderCountry] = useState('asc');
    const [sortOrderComments, setSortOrderComments] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [citiesPerPage, setCitiesPerPage] = useState(10);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedFavorites, setSelectedFavorites] = useState(null);
    const [selectedContinentAllocation, setSelectedContinentAllocation] = useState('');
    const [selectedLanguagesSpoken, setSelectedLanguagesSpoken] = useState([]);
    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showMobileSort, setShowMobileSort] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [draftSortField, setDraftSortField] = useState(null);
    const [draftSortDirection, setDraftSortDirection] = useState(null);

    // Matches the mobile card/filter-modal breakpoint used across Discovery pages.
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const { isMobileNavDocked, mobileMapRowRef } = useMobileDiscoveryNavDocking();

    const [favorites, setFavorites] = useState(new Set());
    const [geocodedCoords, setGeocodedCoords] = useState({});

    const continentAllocationOptions = [
        ...new Set(
            citiesList
                .map((cityItem) => String(cityItem.continent || '').trim())
                .filter(Boolean)
        ),
    ]
        .sort((a, b) => a.localeCompare(b))
        .map((continent) => ({
            value: continent,
            label: continent,
        }));

    const countryOptions = [
        ...new Set(
            citiesList
                .map((cityItem) => String(cityItem.countryName || '').trim())
                .filter(Boolean)
        ),
    ]
        .sort((a, b) => a.localeCompare(b))
        .map((countryName) => ({
            value: countryName,
            label: countryName,
        }));

    const languagesSpokenOptions = [
        ...new Set(
            citiesList.flatMap((cityItem) =>
                Array.isArray(cityItem.languagesSpoken)
                    ? cityItem.languagesSpoken
                        .map((language) => String(language || '').trim())
                        .filter(Boolean)
                    : []
            )
        ),
    ]
        .sort((a, b) => a.localeCompare(b))
        .map((language) => ({
            value: language,
            label: language,
        }));

    const filteredCities = citiesList.filter((cityItem) => {
        const searchText = [
            cityItem.id,
            cityItem.cityName,
            cityItem.countryName,
            cityItem.continent,
            cityItem.comments,
            Array.isArray(cityItem.languagesSpoken) ? cityItem.languagesSpoken.join(' ') : '',
        ]
            .join(' ')
            .toLowerCase();

        const matchesSearch =
            !searchQuery || searchText.includes(searchQuery.toLowerCase());

        const matchesFavorites =
            selectedFavorites === null || favorites.has(String(cityItem._id));

        const matchesContinentAllocation =
            !selectedContinentAllocation ||
            String(cityItem.continent || '').trim() === selectedContinentAllocation;

        const matchesCountry =
            (!countryFilter || String(cityItem.countryName || '').trim() === countryFilter) &&
            (!selectedCountry || String(cityItem.countryName || '').trim() === selectedCountry);

        const matchesLanguagesSpoken =
            !Array.isArray(selectedLanguagesSpoken) ||
            selectedLanguagesSpoken.length === 0 ||
            (
                Array.isArray(cityItem.languagesSpoken) &&
                selectedLanguagesSpoken.some((selectedLanguage) =>
                    cityItem.languagesSpoken.includes(selectedLanguage)
                )
            );

        return (
            matchesSearch &&
            matchesFavorites &&
            matchesContinentAllocation &&
            matchesCountry &&
            matchesLanguagesSpoken
        );
    });

    const citiesWithCoordinates = useMemo(
        () => filteredCities.filter((cityItem) => getCityCoords(cityItem, geocodedCoords) !== null),
        [filteredCities, geocodedCoords]
    );

    const anyCitiesWithCoordinates = useMemo(
        () => citiesList.some((cityItem) => getCityCoords(cityItem, geocodedCoords) !== null),
        [citiesList, geocodedCoords]
    );

    const indexOfLastCity = currentPage * citiesPerPage;
    const indexOfFirstCity = indexOfLastCity - citiesPerPage;
    const currentCities = filteredCities.slice(indexOfFirstCity, indexOfLastCity);

    const selectedLanguagesSpokenKey = useMemo(
        () => selectedLanguagesSpoken.join('|'),
        [selectedLanguagesSpoken]
    );

    const mobileSortedCities = sortField
        ? getMobileSortedCities(filteredCities, sortField, sortDirection)
        : filteredCities;

    const mobileFilterKey = JSON.stringify({
        searchQuery,
        countryFilter,
        selectedCountry,
        selectedFavorites,
        selectedContinentAllocation,
        selectedLanguagesSpoken,
        sortOrderCity,
        sortOrderCountry,
        sortOrderComments,
        sortField,
        sortDirection,
    });
    const mobilePagination = useMobileLoadMore({
        items: mobileSortedCities,
        filterKey: mobileFilterKey,
        storageKey: 'discovery-cities-mobile-list',
        isMobile,
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchQuery,
        selectedCountry,
        selectedFavorites,
        selectedContinentAllocation,
        selectedLanguagesSpokenKey,
    ]);

    const buildCurrentFiltersPayload = () => ({
        searchQuery,
        selectedCountry,
        selectedFavorites,
        selectedContinentAllocation,
        selectedLanguagesSpoken,
    });

    const applySavedFilterSet = (filters = {}) => {
        setSearchQuery(filters.searchQuery || '');
        setSelectedCountry(filters.selectedCountry || '');
        setSelectedFavorites(filters.selectedFavorites === true ? true : null);
        setSelectedContinentAllocation(filters.selectedContinentAllocation || '');
        setSelectedLanguagesSpoken(
            Array.isArray(filters.selectedLanguagesSpoken)
                ? filters.selectedLanguagesSpoken
                : []
        );
    };

    const handleSaveFilters = async (filterSetName) => {
        try {
            const trimmedName = String(filterSetName || '').trim();
            if (!trimmedName) return;

            const payload = {
                page: 'cities',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            };

            const res = await api.post('/api/discovery-filter-sets', payload);
            const savedFilterSet = res.data;

            setSavedFilterSets((prev) => {
                const withoutDuplicate = prev.filter(
                    (item) => item._id !== savedFilterSet._id && item.name !== savedFilterSet.name
                );

                return [savedFilterSet, ...withoutDuplicate];
            });

            setSelectedFilterSet(savedFilterSet._id);
        } catch (error) {
            console.error('Error saving discovery city filter set:', error);
        }
    };

    // preserveSearch: mobile deletes a saved preset without touching the live
    // toolbar search, since Search is independent of staged/saved filter state there.
    // Desktop keeps the previous full-reset behavior.
    const handleDeleteFilterSet = async (filterSetId, { preserveSearch = false } = {}) => {
        if (!filterSetId || filterSetId === 'default') return;

        try {
            await api.delete(`/api/discovery-filter-sets/${filterSetId}`);

            const wasSelected = String(selectedFilterSet) === String(filterSetId);

            setSavedFilterSets((prev) =>
                prev.filter((filterSet) => String(filterSet._id) !== String(filterSetId))
            );

            if (wasSelected) {
                setSelectedFilterSet('default');
                applySavedFilterSet(preserveSearch ? { searchQuery } : {});
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);

        if (value === 'default') {
            applySavedFilterSet({});
            return;
        }

        const selectedSavedFilterSet = savedFilterSets.find(
            (item) => item._id === value
        );

        if (selectedSavedFilterSet?.filters) {
            applySavedFilterSet(selectedSavedFilterSet.filters);
        }
    };

    useEffect(() => {
        trackEvent(ANALYTICS_EVENTS.DISCOVERY_OPENED, {
            discoveryType: 'cities',
            source: 'top_level_discovery',
            level: 1,
        });
    }, []);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        const fetchCitiesAndFilterSets = async () => {
            try {
                const [citiesRes, filterSetsRes, currentUserRes] = await Promise.all([
                    api.get('/api/discovery/cities', { signal }),
                    api.get('/api/discovery-filter-sets?page=cities', { signal }),
                    api.get('/api/users/me', { signal }),
                ]);
                if (!isCurrent()) return;

                const safeCities = (Array.isArray(citiesRes.data) ? citiesRes.data : [])
                    .sort((a, b) => String(a.countryName || '').localeCompare(String(b.countryName || '')));
                const safeFilterSets = Array.isArray(filterSetsRes.data) ? filterSetsRes.data : [];
                const safeFavoriteCities = Array.isArray(currentUserRes?.data?.favoriteCities)
                    ? currentUserRes.data.favoriteCities.map((id) => String(id))
                    : [];

                setCitiesList(refreshSortRef.current ? [...safeCities].sort(refreshSortRef.current) : safeCities);
                setSavedFilterSets(safeFilterSets);
                setFavorites(new Set(safeFavoriteCities));
            } catch (error) {
                if (!isCurrent()) return;
                console.error('Error fetching discovery cities / filter sets / favorites:', error);
            }
        };

        fetchCitiesAndFilterSets();
    }, []);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        if (!citiesList.length || !MAPBOX_TOKEN) return;

        const needsGeocoding = citiesList.filter((city) => {
            const latNum = Number(city.latitude);
            const lngNum = Number(city.longitude);
            return city.latitude == null || city.longitude == null ||
                Number.isNaN(latNum) || Number.isNaN(lngNum) ||
                (latNum === 0 && lngNum === 0);
        });

        if (!needsGeocoding.length) return;

        needsGeocoding.forEach(async (city) => {
            const cityId = String(city._id);
            const cityName = String(city.cityName || city.name || '').trim();
            const countryName = String(city.countryName || '').trim();
            if (!cityName || !countryName) return;

            try {
                const query = encodeURIComponent(`${cityName}, ${countryName}`);
                const res = await fetch(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?types=place&limit=1&access_token=${MAPBOX_TOKEN}`, { signal }
                );
                if (!isCurrent()) return;
                const data = await res.json();
                if (!isCurrent()) return;
                const center = data?.features?.[0]?.center;
                if (Array.isArray(center) && center.length >= 2) {
                    const [geocodedLng, geocodedLat] = center;
                    setGeocodedCoords((prev) => ({
                        ...prev,
                        [cityId]: { lat: geocodedLat, lng: geocodedLng },
                    }));
                }
            } catch {
                if (!isCurrent()) return;
                // silently ignore geocoding failures
            }
        });
    }, [citiesList], { reconnect: false });

    const sortCitiesByName = () => {
        const newSortOrder = sortOrderCity === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const cityA = String(a.cityName || '').toLowerCase();
            const cityB = String(b.cityName || '').toLowerCase();

            return newSortOrder === 'asc'
                ? cityA.localeCompare(cityB)
                : cityB.localeCompare(cityA);
        };
        const sortedCities = [...citiesList].sort(refreshSortRef.current);

        setCitiesList(sortedCities);
        setSortOrderCity(newSortOrder);
    };

    const sortCitiesByCountry = () => {
        const newSortOrder = sortOrderCountry === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const countryA = String(a.countryName || '').toLowerCase();
            const countryB = String(b.countryName || '').toLowerCase();

            return newSortOrder === 'asc'
                ? countryA.localeCompare(countryB)
                : countryB.localeCompare(countryA);
        };
        const sortedCities = [...citiesList].sort(refreshSortRef.current);

        setCitiesList(sortedCities);
        setSortOrderCountry(newSortOrder);
    };

    const sortCitiesByComments = () => {
        const newSortOrder = sortOrderComments === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const commentsA = Number(a.comments) || 0;
            const commentsB = Number(b.comments) || 0;

            return newSortOrder === 'asc'
                ? commentsA - commentsB
                : commentsB - commentsA;
        };
        const sortedCities = [...citiesList].sort(refreshSortRef.current);

        setCitiesList(sortedCities);
        setSortOrderComments(newSortOrder);
    };

    const totalPages = Math.ceil(filteredCities.length / citiesPerPage);
    const noCities = filteredCities.length === 0;

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const getPaginationItems = () => {
        const siblingCount = 2;
        const boundaryCount = 1;

        if (totalPages <= 1) return [1];

        const range = (start, end) => {
            const out = [];
            for (let i = start; i <= end; i++) out.push(i);
            return out;
        };

        const startPages = range(1, Math.min(boundaryCount, totalPages));
        const endPages = range(
            Math.max(totalPages - boundaryCount + 1, boundaryCount + 1),
            totalPages
        );

        const siblingsStart = Math.max(
            Math.min(
                currentPage - siblingCount,
                totalPages - boundaryCount - siblingCount * 2 - 1
            ),
            boundaryCount + 2
        );

        const siblingsEnd = Math.min(
            Math.max(
                currentPage + siblingCount,
                boundaryCount + siblingCount * 2 + 2
            ),
            totalPages - boundaryCount - 1
        );

        const items = [
            ...startPages,
            ...(siblingsStart > boundaryCount + 2
                ? ['ellipsis']
                : boundaryCount + 1 < totalPages - boundaryCount
                    ? [boundaryCount + 1]
                    : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundaryCount - 1
                ? ['ellipsis']
                : totalPages - boundaryCount > boundaryCount
                    ? [totalPages - boundaryCount]
                    : []),
            ...endPages,
        ];

        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const toggleFavorite = async (cityId) => {
        const cityIdStr = String(cityId);
        const previousFavorites = new Set(favorites);
        const wasFavorited = previousFavorites.has(cityIdStr);

        const optimisticFavorites = new Set(previousFavorites);

        if (wasFavorited) {
            optimisticFavorites.delete(cityIdStr);
        } else {
            optimisticFavorites.add(cityIdStr);
        }

        setFavorites(optimisticFavorites);

        try {
            const res = await api.patch(`/api/users/me/favorite-cities/${cityIdStr}`);

            const safeFavoriteCities = Array.isArray(res?.data?.favoriteCities)
                ? res.data.favoriteCities.map((id) => String(id))
                : [];

            setFavorites(new Set(safeFavoriteCities));
        } catch (error) {
            console.error('Error toggling favorite city:', error);
            setFavorites(previousFavorites);
        }
    };

    const discoveryNavItems = [
        { to: '/discovery/countries', eventKey: 'countries', label: 'Countries' },
        { to: '/discovery/cities', eventKey: 'cities', label: 'Cities' },
        { to: '/discovery/companies/jobs', eventKey: 'companies', label: 'Companies & Jobs', alsoActive: ['/discovery/companies', '/discovery/jobs'] },
    ];

    const mobileSortOptions = [
        { field: 'cityName', label: 'City name' },
        { field: 'country', label: 'Country' },
        { field: 'population', label: 'Population' },
    ];
    const activeMobileSortOption = mobileSortOptions.find((option) => option.field === sortField);

    const clearMobileSort = () => {
        setSortField(null);
        setSortDirection(null);
    };

    const openMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(true);
    };

    const closeMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(false);
    };

    const clearMobileSortFromDrawer = () => {
        clearMobileSort();
        setDraftSortField(null);
        setDraftSortDirection(null);
        setShowMobileSort(false);
    };

    const selectDraftSort = (field) => {
        setDraftSortDirection((currentDirection) => (
            draftSortField === field
                ? (currentDirection === 'asc' ? 'desc' : 'asc')
                : mobileCitySortDefinitions[field].defaultDirection
        ));
        setDraftSortField(field);
    };

    const applyMobileSort = () => {
        setSortField(draftSortField);
        setSortDirection(draftSortDirection);
        setShowMobileSort(false);
    };

    const appliedMobileChips = selectedCountry ? [{
        key: 'selectedCountry',
        label: selectedCountry,
        onRemove: () => setSelectedCountry(''),
        removeLabel: `Clear ${selectedCountry} country filter`,
    }] : [];

    if (activeMobileSortOption) {
        appliedMobileChips.push({
            key: 'activeSort',
            label: activeMobileSortOption.label,
            icon: sortDirection === 'asc'
                ? <FaCaretUp aria-hidden="true" />
                : <FaCaretDown aria-hidden="true" />,
            onOpen: openMobileSort,
            onRemove: clearMobileSort,
            removeLabel: `Clear ${activeMobileSortOption.label} sort`,
        });
    }

    // Cities has no company/industry/job data of its own — those are company
    // attributes. Route those to Companies instead of pretending Cities can filter
    // on them. country/city/language are all supported locally via search + language.
    const isCompanySpecificFilter = (sf) =>
        Boolean(sf?.industry) ||
        sf?.jobType === 'student' ||
        sf?.jobType === 'english-speaking' ||
        sf?.jobType === 'internship';

    const getCountryIso2ByName = (countryName) => {
        if (!countryName) return '';

        const normalizedName = String(countryName).trim().toLowerCase();
        const match = countriesData.find(
            (country) => String(country.name || '').trim().toLowerCase() === normalizedName
        );

        return match ? match.id : '';
    };

    // Supported locally on Cities: country/city -> searchQuery (matchesSearch already
    // searches cityName/countryName), language -> selectedLanguagesSpoken.
    const applyStructuredFilters = (sf) => {
        if (!sf) return;

        if (sf.city) {
            setSearchQuery(sf.city);
        } else if (sf.country) {
            setSearchQuery(sf.country);
        }

        if (sf.language) {
            setSelectedLanguagesSpoken((prev) =>
                prev.includes(sf.language) ? prev : [...prev, sf.language]
            );
        }
    };

    const navigateToCompaniesWithFilters = (sf) => {
        const params = new URLSearchParams();

        if (sf.city) params.set('city', sf.city);
        if (sf.industry) params.set('industry', sf.industry);
        if (sf.jobType === 'student') params.set('studentFriendly', 'true');
        if (sf.jobType === 'internship') params.set('internship', 'true');
        if (sf.jobType === 'english-speaking' || sf.language === 'English') {
            params.set('englishSpeaking', 'true');
        }

        const countryIso2 = getCountryIso2ByName(sf.country);
        if (countryIso2) params.set('country', countryIso2);

        navigate(`/discovery/companies?${params.toString()}`);
    };

    const handleChipClick = (sf) => {
        if (!sf) return;

        if (isCompanySpecificFilter(sf)) {
            navigateToCompaniesWithFilters(sf);
            return;
        }

        applyStructuredFilters(sf);
    };

    // Opt this page into the shared mobile AI assistant (header trigger +
    // bottom sheet). No helper-supported filters here, so suggestions are the
    // generic Discovery fallback set.
    useMobileAiPageContext(
        {
            pageType: 'discovery-cities',
            mapFirst: true,
            mapScope: 'global',
            discoveryType: PAGE_DISCOVERY_TYPE,
            suggestions: getDiscoveryAiSuggestions({ discoveryType: PAGE_DISCOVERY_TYPE }),
        },
        { onApplyStructuredFilters: handleChipClick }
    );

    return (
        <>
            <Sidebar />
            <main className={`main-content flex-grow-1 discovery-cities-page discovery-map-page${isMobileNavDocked ? ' is-mobile-nav-docked' : ''}`}>
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col>
                            <Card className="discovery-shell-card">
                                <Card.Body>
                                    <Row className="discovery-page-title-row">
                                        <Col xs={12}>
                                            <h1>Discover</h1>
                                        </Col>
                                    </Row>

                                    {anyCitiesWithCoordinates && (
                                        <Row ref={mobileMapRowRef} className="discovery-map-row">
                                            <Col xs={12} className="px-0">
                                                <div className="discovery-mobile-map-area">
                                                    <DiscoveryMap
                                                        items={citiesWithCoordinates}
                                                        height={260}
                                                        singleItemZoom={6}
                                                        compactSpreadZoom={true}
                                                        groupClickZoom={7}
                                                        spreadZoomOffset={0}
                                                        fitBoundsPadding={60}
                                                        clusterPixelRadius={30}
                                                        markerScale={0.6}
                                                        markerClassName="discovery-map-marker--city"
                                                        canCluster={(a, b) => a.country === b.country}
                                                        getLatitude={(cityItem) => getCityCoords(cityItem, geocodedCoords)?.lat}
                                                        getLongitude={(cityItem) => getCityCoords(cityItem, geocodedCoords)?.lng}
                                                        getTitle={(cityItem) => cityItem.cityName || cityItem.name || 'City'}
                                                        getSubtitle={(cityItem) => cityItem.countryName || ''}
                                                        getLink={(cityItem) => `/discovery/cities/${cityItem._id}`}
                                                        ctaLabel="Explore city →"
                                                        getSecondaryLink={(cityItem) => `/discovery/cities/${cityItem._id}/companies`}
                                                        secondaryCtaLabel="View companies →"
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className="mb-4 discovery-tabs-row">
                                        <Col>
                                            <NavTabs
                                                items={discoveryNavItems}
                                                className="discovery-route-tabs"
                                                autoRevealActive
                                                trackOverflow
                                            />
                                        </Col>
                                    </Row>


                                    <DiscoveryDesktopAiSlot />

                                    {!isMobile && (
                                        <Row className="mb-3">
                                            <Col xs={12}>
                                                <DiscoveryFilterBar
                                                    title="City"
                                                    variant="countries"
                                                    items={citiesList}
                                                    searchQuery={searchQuery}
                                                    setSearchQuery={setSearchQuery}
                                                    selectedFavorites={selectedFavorites}
                                                    setSelectedFavorites={setSelectedFavorites}
                                                    selectedContinentAllocation={selectedContinentAllocation}
                                                    setSelectedContinentAllocation={setSelectedContinentAllocation}
                                                    continentAllocationOptions={continentAllocationOptions}
                                                    selectedLanguagesSpoken={selectedLanguagesSpoken}
                                                    setSelectedLanguagesSpoken={setSelectedLanguagesSpoken}
                                                    languagesSpokenOptions={languagesSpokenOptions}
                                                    savedFilterSets={savedFilterSets}
                                                    selectedFilterSet={selectedFilterSet}
                                                    onFilterSetChange={handleFilterSetChange}
                                                    onDeleteFilterSet={handleDeleteFilterSet}
                                                    onSaveFilters={handleSaveFilters}
                                                />
                                            </Col>
                                        </Row>
                                    )}

                                    {isMobile && (
                                        <>
                                            <MobileDiscoveryHeading title="Cities" />

                                            <MobileDiscoverySearchToolbar
                                                searchValue={searchQuery}
                                                onSearchChange={setSearchQuery}
                                                searchPlaceholder="Search..."
                                                searchAriaLabel="Search cities"
                                                actions={[
                                                    {
                                                        label: 'Open filters',
                                                        icon: icFilter,
                                                        onClick: () => setShowMobileFilters(true),
                                                    },
                                                    {
                                                        label: 'Sort cities',
                                                        icon: icSort,
                                                        onClick: openMobileSort,
                                                    },
                                                ]}
                                            />

                                            <MobileAppliedChips chips={appliedMobileChips} />

                                            <DiscoveryFilterBar
                                                title="City"
                                                variant="countries"
                                                items={citiesList}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                selectedCountry={selectedCountry}
                                                setSelectedCountry={setSelectedCountry}
                                                countryOptions={countryOptions}
                                                selectedFavorites={selectedFavorites}
                                                setSelectedFavorites={setSelectedFavorites}
                                                selectedContinentAllocation={selectedContinentAllocation}
                                                setSelectedContinentAllocation={setSelectedContinentAllocation}
                                                continentAllocationOptions={continentAllocationOptions}
                                                selectedLanguagesSpoken={selectedLanguagesSpoken}
                                                setSelectedLanguagesSpoken={setSelectedLanguagesSpoken}
                                                languagesSpokenOptions={languagesSpokenOptions}
                                                savedFilterSets={savedFilterSets}
                                                selectedFilterSet={selectedFilterSet}
                                                onFilterSetChange={handleFilterSetChange}
                                                onDeleteFilterSet={(filterSetId) => handleDeleteFilterSet(filterSetId, { preserveSearch: true })}
                                                onSaveFilters={handleSaveFilters}
                                                mobileModalOnly
                                                mobileModalOpen={showMobileFilters}
                                                onMobileModalClose={() => setShowMobileFilters(false)}
                                                mobileModalTitle="Filter cities"
                                                mobileApplyLabel="Apply filters"
                                                hideTypeToggleOnMobile
                                                hideActiveFilterSummaryOnMobile
                                                useFullScreenMobilePanel
                                                mobileSavedFilterLabel="Saved filter"
                                                mobileSaveFilterLabel="Save current filters"
                                                compactSavedFilterOnMobile
                                                hideSearchInMobileModal
                                            />
                                        </>
                                    )}

                                    {noCities ? (
                                        <Row>
                                            <Col md={12}>
                                                <p className="mb-0">No cities available.</p>
                                            </Col>
                                        </Row>
                                    ) : isMobile ? (
                                        <div className="mobile-company-list">
                                            {mobilePagination.mobileItems.map((cityItem) => (
                                                <MobileDiscoveryCityCard
                                                    key={cityItem._id}
                                                    city={cityItem}
                                                    isFavorite={favorites.has(String(cityItem._id))}
                                                    onFavoriteToggle={toggleFavorite}
                                                    onOpenCity={(selectedCity) => {
                                                        mobilePagination.rememberScrollPosition();
                                                        navigate(`/discovery/cities/${selectedCity._id}`);
                                                    }}
                                                />
                                            ))}
                                            <MobileLoadMoreFooter
                                                visibleCount={mobilePagination.visibleCount}
                                                totalCount={mobilePagination.totalCount}
                                                itemLabel="cities"
                                                onLoadMore={mobilePagination.loadMore}
                                                isLoading={mobilePagination.isLoadingMore}
                                                hasMore={mobilePagination.hasMore}
                                            />
                                        </div>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table className="discovery-table discovery-table--cities">
                                                        <thead>
                                                            <tr>
                                                                <th>ID</th>

                                                                <th onClick={sortCitiesByName} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        City Region
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th onClick={sortCitiesByCountry} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Country
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th>
                                                                    <div>Continent allocation</div>
                                                                </th>

                                                                <th>Favorite</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentCities.map((cityItem) => (
                                                                <tr className="company-row" key={cityItem._id} onClick={() => navigate(`/discovery/cities/${cityItem._id}`)} style={{ cursor: 'pointer' }}>
                                                                    <td>{cityItem.id || '—'}</td>
                                                                    <td>{cityItem.cityName || '—'}</td>
                                                                    <td>{cityItem.countryName || '—'}</td>
                                                                    <td>{cityItem.continent || '—'}</td>
                                                                    <td>
                                                                        <span
                                                                            className="cursor-pointer d-inline-flex align-items-center"
                                                                            style={{ cursor: 'pointer' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFavorite(cityItem._id);
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={favorites.has(String(cityItem._id)) ? icHeartFull : icHeartEmpty}
                                                                                alt="favorite"
                                                                                style={{ width: '16px', height: '16px' }}
                                                                            />
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {!isMobile && (
                                    <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center">
                                            <span>
                                                Total {filteredCities.length} {filteredCities.length === 1 ? 'city' : 'cities'}
                                            </span>
                                            <Pagination>
                                                <Pagination.Prev
                                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    <BsChevronLeft />
                                                </Pagination.Prev>

                                                {getPaginationItems().map((item, index) =>
                                                    item === 'ellipsis' ? (
                                                        <Pagination.Ellipsis key={`e-${index}`} disabled />
                                                    ) : (
                                                        <Pagination.Item
                                                            key={item}
                                                            active={item === currentPage}
                                                            onClick={() => handlePageChange(item)}
                                                        >
                                                            {item}
                                                        </Pagination.Item>
                                                    )
                                                )}

                                                <Pagination.Next
                                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    <BsChevronRight />
                                                </Pagination.Next>
                                            </Pagination>
                                        </Col>

                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={citiesPerPage}
                                                    onChange={(e) => {
                                                        setCitiesPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>

                                            <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                                Go to
                                            </label>

                                            <Form.Control
                                                id="goto-page"
                                                className="pagination-controls-goto-input"
                                                type="number"
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages || 1);
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Modal
                className="with-form discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen discovery-sort-mobile-modal"
                show={showMobileSort}
                onHide={closeMobileSort}
            >
                <div className="discovery-filter-mobile-panel">
                    <div className="discovery-filter-mobile-panel__header">
                        <Modal.Title>Sort cities</Modal.Title>
                        <div className="discovery-filter-mobile-panel__header-actions">
                            <button type="button" className="discovery-filter-mobile-panel__close" onClick={closeMobileSort} aria-label="Close sort cities">
                                &times;
                            </button>
                        </div>
                    </div>

                    <Modal.Body>
                        <div className="discovery-sort-mobile-options" role="radiogroup" aria-label="Sort cities by">
                            {mobileSortOptions.map((option) => {
                                const isActive = draftSortField === option.field;
                                return (
                                    <button
                                        key={option.field}
                                        type="button"
                                        role="radio"
                                        className={`discovery-sort-mobile-option${isActive ? ' is-active' : ''}`}
                                        onClick={() => selectDraftSort(option.field)}
                                        aria-checked={isActive}
                                    >
                                        <span className="discovery-sort-mobile-option__label">
                                            <span className="discovery-sort-mobile-option__radio" aria-hidden="true" />
                                            <span>{option.label}</span>
                                        </span>
                                        {isActive && (
                                            <span className="discovery-sort-mobile-option__direction" aria-label={draftSortDirection === 'asc' ? 'Ascending' : 'Descending'}>
                                                <span>{draftSortDirection === 'asc' ? 'Ascending' : 'Descending'}</span>
                                                {draftSortDirection === 'asc' ? <FaCaretUp aria-hidden="true" /> : <FaCaretDown aria-hidden="true" />}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </Modal.Body>

                    <div className="discovery-filter-mobile-panel__footer">
                        <Button
                            type="button"
                            variant="outline-secondary"
                            className="discovery-filter-mobile-panel__reset"
                            onClick={clearMobileSortFromDrawer}
                        >
                            Clear sort
                        </Button>

                        <Button
                            type="button"
                            variant="primary"
                            className="discovery-filter-mobile-panel__apply"
                            onClick={applyMobileSort}
                        >
                            Apply sort
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default DiscoverCityList;
